// ================================
// Роли пользователей системы
// Синхронизировано с бэкендом
// ================================

/**
 * Enum ролей пользователя
 * Числовые ID должны совпадать с бэкендом
 */
export enum UserRole {
    ADMIN = 1,
    USER = 2,
    MANAGER = 6,
    DIRECTOR = 7,
    SUPPORT = 8,
    DOWNLOAD_PRODUCTS = 9
}

/**
 * Названия ролей для отображения в UI
 */
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
    [UserRole.ADMIN]: 'Администратор',
    [UserRole.USER]: 'Пользователь',
    [UserRole.MANAGER]: 'Менеджер',
    [UserRole.DIRECTOR]: 'Директор',
    [UserRole.SUPPORT]: 'Техподдержка',
    [UserRole.DOWNLOAD_PRODUCTS]: 'Загрузка товаров'
};

/**
 * Группы ролей для удобных проверок доступа
 */
export const ROLE_GROUPS = {
    // Высшее руководство
    MANAGEMENT: [UserRole.ADMIN, UserRole.DIRECTOR],

    // Операционный персонал
    STAFF: [UserRole.MANAGER, UserRole.SUPPORT, UserRole.DOWNLOAD_PRODUCTS],

    // Все сотрудники (кроме обычных пользователей)
    EMPLOYEES: [UserRole.ADMIN, UserRole.MANAGER, UserRole.DIRECTOR, UserRole.SUPPORT, UserRole.DOWNLOAD_PRODUCTS],

    // Работа с товарами
    PRODUCT_MANAGEMENT: [UserRole.MANAGER, UserRole.DOWNLOAD_PRODUCTS],

    // Технические роли
    TECHNICAL: [UserRole.ADMIN, UserRole.SUPPORT]
} as const;

/**
 * Уровни доступа ролей (для сравнения)
 */
export const ROLE_LEVELS: Record<UserRole, number> = {
    [UserRole.ADMIN]: 100,
    [UserRole.DIRECTOR]: 90,
    [UserRole.MANAGER]: 80,
    [UserRole.SUPPORT]: 70,
    [UserRole.DOWNLOAD_PRODUCTS]: 60,
    [UserRole.USER]: 50
};

// Хелпер функции
/**
 * Получить отображаемое имя роли
 */
export const getRoleDisplayName = (roleId: UserRole): string => {
    return ROLE_DISPLAY_NAMES[roleId] || 'Неизвестная роль';
};

/**
 * Проверить, является ли роль валидной
 */
export const isValidRole = (roleId: number): roleId is UserRole => {
    return Object.values(UserRole).includes(roleId as UserRole);
};

/**
 * Получить название роли по ID
 */
export const getRoleName = (roleId: UserRole): keyof typeof UserRole | undefined => {
    return UserRole[roleId] as keyof typeof UserRole;
};

/**
 * Проверить, имеет ли пользователь минимальный уровень доступа
 */
export const hasMinRoleLevel = (userRoleId: UserRole, minLevel: number): boolean => {
    return ROLE_LEVELS[userRoleId] >= minLevel;
};

/**
 * Получить самые высокие роли пользователя
 */
export const getHighestUserRole = (userRoles: UserRole[]): UserRole | null => {
    if (userRoles.length === 0) return null;

    return userRoles.reduce((highest, role) => {
        return ROLE_LEVELS[role] > ROLE_LEVELS[highest] ? role : highest;
    }, userRoles[0]);
};